import API from 'constants/api';
import Api from 'util/Api';
import {
  assertOk,
  BlockingPackage,
  MassiveConflictError,
  toMassiveError,
} from './MassiveErrors';

export const MASSIVE_STATUS = {
  SPAWNING: 'spawning',
  CREATING: 'waiting_creation',
  LABELING: 'labeling',
  READY: 'ready',
  PARTIAL: 'partial',
  FAILED: 'failed',
  CANCELLED: 'cancelled',
  STOPPED: 'stopped',
} as const;

export type MassiveStatus =
  (typeof MASSIVE_STATUS)[keyof typeof MASSIVE_STATUS];

export const OWNER_TYPE = {
  STORE: 20,
  MARKETPLACE: 40,
  LOGISTIC_OPERATOR: 50,
  MARKETPLACE_AND_OPL: 60,
} as const;

export interface MassiveCreator {
  owner_type: number;
  owner_id: number;
}

export interface MassiveOwner {
  creator: MassiveCreator;
  tenant_id: number;
  owner_ids: number[] | null;
  store_ids: number[] | null;
}

export interface MassiveItemState {
  row: number;
  tracking: string;
  store_id: number;
  dali_internal_id?: number;
  creation_status: string;
  creation_reason?: string;
  label_status: string;
}

export interface MassiveLabelsMeta {
  available: boolean;
  generated_at?: string;
  count?: number;
}

export interface Massive {
  massive_id: string;
  owner: MassiveOwner;
  uploader_email: string;
  status: MassiveStatus;
  total: number;
  created_count: number;
  failed_count: number;
  rejected_count: number;
  missing_labels: number;
  items: MassiveItemState[];
  labels: MassiveLabelsMeta;
  last_error?: string;
  created_at: string;
  deleted_at?: string;
  deleted_by_email?: string;
}

export interface StoreRef {
  id: number;
  name: string;
}

export interface MassiveListItem {
  massive_id: string;
  status: MassiveStatus;
  total: number;
  created_count: number;
  failed_count: number;
  rejected_count: number;
  missing_labels: number;
  creator: MassiveCreator | null;
  store_ids: number[] | null;
  stores: StoreRef[] | null;
  uploader_email: string;
  created_at: string;
  last_error?: string;
  labels_available: boolean;
}

export interface MassiveListResponse {
  content: MassiveListItem[];
  total: number;
  page: number;
  size: number;
}

export interface FieldError {
  row: number;
  column: string;
  value: string;
  code: string;
  message: string;
}

export interface RowResult {
  row: number;
  tracking?: string;
  errors: FieldError[];
}

export interface CreateMassiveResponse {
  massive_id?: string;
  total: number;
  accepted: number;
  rejected: number;
  errors?: RowResult[];
  applied_defaults?: Record<string, number>;
}

export interface MassiveListFilters {
  from?: string;
  to?: string;
  code?: string;
  marketplaceId?: number;
  storeId?: number;
  status?: MassiveStatus;
  page?: number;
  size?: number;
}

export interface UploadOptions {
  file: File;
  marketplaceId?: number;
  storeId?: number;
  allowPartial: boolean;
  defaultShipmentType?: string;
  defaultDeliveryTerm?: string;
  dryRun?: boolean;
}

const uploadForm = (options: UploadOptions): FormData => {
  const form = new FormData();
  form.append('file', options.file);
  form.append('allow_partial', String(options.allowPartial));
  if (options.marketplaceId) {
    form.append('marketplace_id', String(options.marketplaceId));
  }
  if (options.storeId) {
    form.append('store_id', String(options.storeId));
  }
  if (options.defaultShipmentType) {
    form.append('default_shipment_type', options.defaultShipmentType);
  }
  if (options.defaultDeliveryTerm) {
    form.append('default_delivery_term', options.defaultDeliveryTerm);
  }
  return form;
};

export const createMassive = async (
  options: UploadOptions,
): Promise<CreateMassiveResponse> => {
  const query = options.dryRun ? '?dry_run=true' : '';
  try {
    const response = await Api.apiAxios.post(
      `${API.segaMassive.root}${query}`,
      uploadForm(options),
    );
    if (response.status === 422) {
      return response.data;
    }
    return assertOk(response);
  } catch (error) {
    throw toMassiveError(error);
  }
};

export const downloadErrorReport = async (
  options: UploadOptions,
): Promise<Blob> => {
  const query = options.dryRun ? '?dry_run=true&format=xlsx' : '?format=xlsx';
  try {
    return assertOk(
      await Api.apiAxios.post(
        `${API.segaMassive.root}${query}`,
        uploadForm(options),
        { responseType: 'blob' },
      ),
    );
  } catch (error) {
    throw toMassiveError(error);
  }
};

export const downloadTemplate = async (): Promise<Blob> => {
  try {
    return assertOk(
      await Api.apiAxios.get(API.segaMassive.template, {
        responseType: 'blob',
      }),
    );
  } catch (error) {
    throw toMassiveError(error);
  }
};

export const fetchMassiveList = async (
  filters: MassiveListFilters,
): Promise<MassiveListResponse> => {
  const params = new URLSearchParams();
  if (filters.from) params.append('from', filters.from);
  if (filters.to) params.append('to', filters.to);
  if (filters.code) params.append('code', filters.code);
  if (filters.marketplaceId) {
    params.append('marketplace_id', String(filters.marketplaceId));
  }
  if (filters.storeId) params.append('store_id', String(filters.storeId));
  if (filters.status) params.append('status', filters.status);
  params.append('page', String(filters.page ?? 0));
  params.append('size', String(filters.size ?? 20));

  try {
    return assertOk(
      await Api.apiAxios.get(`${API.segaMassive.root}?${params.toString()}`),
    );
  } catch (error) {
    throw toMassiveError(error);
  }
};

export const fetchMassive = async (massiveId: string): Promise<Massive> => {
  try {
    return assertOk(await Api.apiAxios.get(API.segaMassive.byId(massiveId)));
  } catch (error) {
    throw toMassiveError(error);
  }
};

export interface MassivePackagesPage {
  content: MassiveItemState[];
  total: number;
  page: number;
  size: number;
}

export const fetchMassivePackages = async (
  massiveId: string,
  search = '',
  page = 0,
  size = 10,
): Promise<MassivePackagesPage> => {
  const params = new URLSearchParams({
    page: String(page),
    size: String(size),
  });
  if (search) params.append('search', search);
  try {
    return assertOk<MassivePackagesPage>(
      await Api.apiAxios.get(
        `${API.segaMassive.packages(massiveId)}?${params.toString()}`,
      ),
    );
  } catch (error) {
    throw toMassiveError(error);
  }
};

export const fetchMassiveErrors = async (
  massiveId: string,
): Promise<RowResult[]> => {
  try {
    const data = assertOk<{ errors?: RowResult[] }>(
      await Api.apiAxios.get(API.segaMassive.errors(massiveId)),
    );
    return data.errors ?? [];
  } catch (error) {
    throw toMassiveError(error);
  }
};

export const downloadMassiveErrors = async (
  massiveId: string,
): Promise<Blob> => {
  try {
    return assertOk(
      await Api.apiAxios.get(
        `${API.segaMassive.errors(massiveId)}?format=xlsx`,
        {
          responseType: 'blob',
        },
      ),
    );
  } catch (error) {
    throw toMassiveError(error);
  }
};

export const downloadMassiveLabels = async (
  massiveId: string,
  format: 'pdf' | 'zpl',
): Promise<Blob> => {
  const response = await Api.apiAxios.get(
    `${API.segaMassive.labels(massiveId)}?format=${format}`,
    { responseType: 'blob' },
  );
  if (response.status === 202) {
    return response.data;
  }
  try {
    return assertOk(response);
  } catch (error) {
    throw toMassiveError(error);
  }
};

export const downloadPackageLabel = async (
  massiveId: string,
  tracking: string,
  format: 'pdf' | 'zpl',
): Promise<Blob> => {
  try {
    return assertOk(
      await Api.apiAxios.get(
        `${API.segaMassive.packageLabel(massiveId, tracking)}?format=${format}`,
        { responseType: 'blob' },
      ),
    );
  } catch (error) {
    throw toMassiveError(error);
  }
};

const conflictFrom = (status?: number, data?: unknown) => {
  if (status !== 409) return null;
  const blocking =
    (data as { blocking_packages?: BlockingPackage[] })?.blocking_packages ??
    [];
  return new MassiveConflictError(blocking);
};

export const cancelMassive = async (
  massiveId: string,
  keepBlocked = false,
): Promise<void> => {
  const url = keepBlocked
    ? `${API.segaMassive.cancel(massiveId)}?keep_blocked=true`
    : API.segaMassive.cancel(massiveId);
  let response;
  try {
    response = await Api.apiAxios.post(url);
  } catch (error) {
    const failure = error as { response?: { status?: number; data?: unknown } };
    const conflict = conflictFrom(
      failure.response?.status,
      failure.response?.data,
    );
    throw conflict ?? toMassiveError(error);
  }
  const conflict = conflictFrom(response.status, response.data);
  if (conflict) throw conflict;
  assertOk(response);
};

export const cancelMassivePackage = async (
  massiveId: string,
  tracking: string,
): Promise<void> => {
  try {
    assertOk(
      await Api.apiAxios.post(
        API.segaMassive.cancelPackage(massiveId, tracking),
      ),
    );
  } catch (error) {
    throw toMassiveError(error);
  }
};
